import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { APIService } from 'src/api/api';
import { getFormattedDateTime12Hour, hasAdminLikeAccess } from 'src/utils/getInitials';
import shouldDisplayDetailField from 'src/utils/detailFieldUtils';
import useAuth from 'src/context/AuthContext';
import NewDispenser from 'src/components/dispensers/NewDispenser';

const formatLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '--';
  if (typeof value === 'boolean') return value ? 'ON' : 'OFF';
  if (typeof value === 'string' && value.includes('T')) {
    return getFormattedDateTime12Hour(value) || value;
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return value;
};

const DispenserDetailsDialog = ({
  open, onClose, dispenserId, dispenserLabel
}) => {
  const { getUser } = useAuth();
  const canEdit = hasAdminLikeAccess(getUser);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [editing, setEditing] = useState(false);

  const loadDetails = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await APIService.getDispenserDetails(dispenserId);
      if (!response?.success || !response.result) throw new Error('Dispenser details are unavailable.');
      setDetails(response.result);
    } catch (error) {
      setLoadError(error?.message || 'Dispenser details are unavailable.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && dispenserId) {
      setEditing(false);
      loadDetails();
    }
  }, [open, dispenserId]);

  const closeDialog = () => {
    setEditing(false);
    onClose();
  };

  const detailEntries = Object.entries(details || {}).filter(([key]) => shouldDisplayDetailField(key));

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      fullWidth
      maxWidth={false}
      aria-labelledby={`transaction-dispenser-details-dialog-title-${dispenserId}`}
      PaperProps={{
        sx: {
          height: { xs: 'calc(100vh - 16px)', md: '80vh' },
          maxHeight: { xs: 'calc(100vh - 16px)', md: '80vh' },
          maxWidth: 'none',
          width: { xs: 'calc(100vw - 16px)', md: '80vw', xl: '1400px' },
        },
      }}
    >
      <DialogTitle
        id={`transaction-dispenser-details-dialog-title-${dispenserId}`}
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          minHeight: 60,
          px: { xs: 2, md: 3 },
          py: 1.25,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ color: '#0F172A', fontSize: { xs: 17, md: 20 }, fontWeight: 800 }}>
            {editing ? 'Edit dispenser' : (details?.station_name || details?.address || dispenserLabel || 'Dispenser details')}
          </Typography>
          <Typography sx={{
            color: '#64748B', fontSize: 12, fontWeight: 700, mt: 0.25
          }}
          >
            {editing ? 'Update dispenser configuration' : 'Complete dispenser overview'}
          </Typography>
        </Box>
        <IconButton aria-label="Close dispenser details" onClick={closeDialog} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 0, overflowY: 'auto', p: { xs: 2, md: 3 } }}>
        {editing && details && (
          <NewDispenser
            open
            embedded
            initialValues={details}
            handleClose={() => setEditing(false)}
            onSaved={async () => {
              setEditing(false);
              await loadDetails();
            }}
          />
        )}
        {loading && (
          <Box sx={{
            alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: 260
          }}
          >
            <CircularProgress size={28} />
          </Box>
        )}
        {loadError && (
          <Box sx={{
            alignItems: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 260, textAlign: 'center'
          }}
          >
            <Typography sx={{ color: '#64748B' }}>{loadError}</Typography>
          </Box>
        )}
        {(!editing || !details) && !loading && !loadError && (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' } }}>
            {detailEntries.map(([key, value]) => (
              <Box
                key={key}
                sx={{
                  border: '1px solid', borderColor: 'divider', borderRadius: 2, minHeight: 78, p: 2
                }}
              >
                <Typography sx={{
                  color: '#64748B', fontSize: 11, fontWeight: 800, letterSpacing: 0.6, mb: 0.75, textTransform: 'uppercase'
                }}
                >
                  {formatLabel(key)}
                </Typography>
                {key === 'status' ? (
                  <Chip label={value ? 'Active' : 'Inactive'} color={value ? 'success' : 'error'} size="small" variant="outlined" />
                ) : (
                  <Typography sx={{
                    color: '#334155', fontSize: 14, fontWeight: 700, whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                  }}
                  >
                    {formatValue(value)}
                  </Typography>
                )}
              </Box>
            ))}
            {canEdit && details && (
              <Box sx={{
                alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', gridColumn: { xs: 'auto', sm: '1 / -1' }, justifyContent: 'flex-end', p: 2
              }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => setEditing(true)}
                  sx={{
                    background: '#2563EB', border: 0, borderRadius: 1.5, color: '#FFF', cursor: 'pointer', fontSize: 14, fontWeight: 700, px: 2, py: 1.1, '&:hover': { background: '#1D4ED8' }
                  }}
                >
                  Edit dispenser
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

DispenserDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dispenserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  dispenserLabel: PropTypes.string,
};

export default DispenserDetailsDialog;
